/**
 * Trajectoire View Controller (EPIC-047.4)
 *
 * Page /pilotage-trajectoire.html. Loads the trajectory + live comparison
 * for the year (defaults to current year, overridable via ?year=N), then
 * hydrates the 3 KPI cards, the monthly table and the SVG chart. The
 * sidebar (mode active + summary) is also refreshed from the API payload.
 *
 * The chart is rendered by the `trajectoire-chart` controller mounted on
 * the same `.nm-ph` element — we get its instance through the Stimulus
 * application registry and call `setData()`.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

const MONTH_NAMES_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const MODE_LABELS = {
  uniform: "Uniforme",
  seasonal: "Saisonnée automatique",
  manual: "Mois par mois",
};

const MODE_DESCRIPTIONS = {
  uniform: "Objectif annuel divisé en 12 mois égaux.",
  seasonal: "Reproduit la saisonnalité de votre CA passé (12 mois d'historique).",
  manual: "Vous avez saisi manuellement l'objectif de chaque mois.",
};

export default class TrajectoireViewController extends BaseController {
  static targets = [
    "title", "subtitle", "editLink", "nextYearLink",
    "kpiCaYtd", "kpiTargetYtd", "kpiGap", "kpiGapHint",
    "chart", "tableBody",
    "modeName", "modeDescription",
    "summary", "summaryTotal", "summaryAvg", "summaryVsLast", "summaryActivity",
    "errorBanner", "errorTitle", "errorMessage",
  ];

  async connect() {
    const params = new URLSearchParams(window.location.search);
    this._year = parseInt(params.get("year"), 10) || new Date().getFullYear();
    await this.load();
  }

  async load() {
    try {
      const data = await api.get("/me/trajectory", { year: String(this._year) });
      this.hydrate(data);
    } catch (error) {
      if (error.status === 404) {
        this.showEmptyState();
        return;
      }
      this.showError(error.message || "Erreur réseau.");
    }
  }

  hydrate(payload) {
    const trajectory = payload.trajectory || payload;
    const comparison = payload.comparison || {};
    const year = trajectory.year || this._year;
    const monthly = Array.isArray(trajectory.monthlyDistribution) ? trajectory.monthlyDistribution : [];
    const monthlyReal = Array.isArray(comparison.monthlyReal) ? comparison.monthlyReal : [];
    const total = parseFloat(trajectory.totalTargetHt) || 0;
    const currentMonth = Number.isInteger(comparison.currentMonth)
      ? comparison.currentMonth
      : (new Date().getFullYear() === year ? new Date().getMonth() + 1 : 12);

    if (this.hasTitleTarget) this.titleTarget.textContent = `Trajectoire CA ${year}`;
    if (this.hasSubtitleTarget) {
      const modeLabel = MODE_LABELS[trajectory.distributionMode] || trajectory.distributionMode || "";
      this.subtitleTarget.textContent =
        `Objectif annuel ventilé sur 12 mois — comparé au CA HT réellement encaissé. Mode actif : ${modeLabel.toLowerCase()}.`;
    }
    if (this.hasEditLinkTarget) {
      this.editLinkTarget.setAttribute("href", `/pilotage-trajectoire-edition.html?trajectoryYear=${year}`);
    }
    if (this.hasNextYearLinkTarget) {
      // « Définir 20YY+1 » : visible uniquement à partir du 1ᵉʳ octobre de
      // l'année civile en cours, et seulement quand on consulte cette année
      // (pas une année passée/future). C-05, décision PO 2026-06-10.
      const now = new Date();
      const showNextYear = year === now.getFullYear() && now.getMonth() + 1 >= 10;
      if (showNextYear) {
        this.nextYearLinkTarget.setAttribute("href", `/pilotage-trajectoire-edition.html?trajectoryYear=${year + 1}`);
        this.nextYearLinkTarget.textContent = `Définir ${year + 1}`;
        this.nextYearLinkTarget.hidden = false;
      } else {
        this.nextYearLinkTarget.hidden = true;
      }
    }

    this.hydrateKpis(comparison);
    this.hydrateTable(monthly, monthlyReal, total, currentMonth);
    this.hydrateChart(monthly, monthlyReal, year);
    this.hydrateSidebar(trajectory, comparison);
  }

  hydrateKpis(comparison) {
    const caReal = parseFloat(comparison.caRealYtd) || 0;
    const target = parseFloat(comparison.expectedYtd) || 0;
    const gapAbs = parseFloat(comparison.gapAbs);
    const gapPct = parseFloat(comparison.gapPct);

    if (this.hasKpiCaYtdTarget) this.kpiCaYtdTarget.innerHTML = `${formatNumber(caReal)} <small>€</small>`;
    if (this.hasKpiTargetYtdTarget) this.kpiTargetYtdTarget.innerHTML = `${formatNumber(target)} <small>€</small>`;
    if (this.hasKpiGapTarget) {
      const sign = Number.isFinite(gapAbs) && gapAbs < 0 ? "− " : (gapAbs > 0 ? "+ " : "");
      const value = Number.isFinite(gapAbs) ? formatNumber(Math.abs(gapAbs)) : "—";
      this.kpiGapTarget.innerHTML = `${sign}${value} <small>€</small>`;
    }
    if (this.hasKpiGapHintTarget) {
      if (!Number.isFinite(gapPct)) {
        this.kpiGapHintTarget.textContent = "—";
        this.kpiGapHintTarget.className = "nm-stat__d";
      } else {
        const sign = gapPct < 0 ? "− " : (gapPct > 0 ? "+ " : "");
        this.kpiGapHintTarget.textContent = `${sign}${formatPercent(Math.abs(gapPct))} ${gapPct < 0 ? "sous l'objectif" : "au-dessus de l'objectif"}`;
        this.kpiGapHintTarget.className = `nm-stat__d ${gapPct < 0 ? "is-warning" : "is-success"}`;
      }
    }
  }

  hydrateTable(monthly, monthlyReal, total, currentMonth) {
    if (!this.hasTableBodyTarget) return;
    const tbody = this.tableBodyTarget;
    while (tbody.firstChild) tbody.removeChild(tbody.firstChild);

    let cumulRealForTotal = 0;

    for (let i = 0; i < 12; i += 1) {
      const monthIndex = i + 1;
      const target = parseFloat(monthly[i]) || 0;
      const realRaw = monthlyReal[i];
      const isFuture = monthIndex > currentMonth || !Number.isFinite(parseFloat(realRaw));

      if (isFuture) {
        const fragment = this.cloneTemplate("tpl-trajectoire-row-future");
        this.fillSlots(fragment, {
          month: MONTH_NAMES_FR[i],
          target: formatEur(target),
        });
        tbody.appendChild(fragment);
      } else {
        const real = parseFloat(realRaw) || 0;
        cumulRealForTotal += real;
        const gapAbs = real - target;
        const gapPct = target > 0 ? (gapAbs / target) * 100 : 0;

        const fragment = this.cloneTemplate("tpl-trajectoire-row");
        this.fillSlots(fragment, {
          month: MONTH_NAMES_FR[i],
          target: formatEur(target),
          real: formatEur(real),
          gapAbs: formatSignedEur(gapAbs),
          gapPct: formatSignedPercent(gapPct),
        });
        const cells = fragment.querySelectorAll("td.is-amount");
        if (gapAbs < 0 && cells.length >= 4) {
          cells[2].classList.add("text-error");
          cells[3].classList.add("text-error");
        } else if (gapAbs > 0 && cells.length >= 4) {
          cells[2].classList.add("text-success");
          cells[3].classList.add("text-success");
        }
        tbody.appendChild(fragment);
      }
    }

    const realTotal = cumulRealForTotal;
    const gapTotal = realTotal - total;
    const gapTotalPct = total > 0 ? (gapTotal / total) * 100 : 0;

    const totalFragment = this.cloneTemplate("tpl-trajectoire-row-total");
    this.fillSlots(totalFragment, {
      target: formatEur(total),
      real: formatEur(realTotal),
      gapAbs: formatSignedEur(gapTotal),
      gapPct: formatSignedPercent(gapTotalPct),
    });
    const totalCells = totalFragment.querySelectorAll("td.is-amount");
    if (gapTotal < 0 && totalCells.length >= 4) {
      totalCells[2].classList.add("text-error");
      totalCells[3].classList.add("text-error");
    } else if (gapTotal > 0 && totalCells.length >= 4) {
      totalCells[2].classList.add("text-success");
      totalCells[3].classList.add("text-success");
    }
    tbody.appendChild(totalFragment);
  }

  hydrateChart(monthly, monthlyReal, year) {
    if (!this.hasChartTarget) return;
    const chartCtrl = this.application.getControllerForElementAndIdentifier(
      this.chartTarget,
      "trajectoire-chart",
    );
    if (!chartCtrl) return;
    chartCtrl.setData({
      monthlyTarget: monthly.map((v) => parseFloat(v) || 0),
      // Préserver `null` pour les mois futurs : le chart arrête la courbe
      // réalisé au dernier mois renseigné (pas de prolongation à 0).
      monthlyReal: monthlyReal.map((v) => {
        if (v === null || v === undefined) return null;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
      }),
      year: String(year),
    });
  }

  hydrateSidebar(trajectory, comparison) {
    const mode = trajectory.distributionMode;
    if (this.hasModeNameTarget) this.modeNameTarget.textContent = MODE_LABELS[mode] || mode || "—";
    if (this.hasModeDescriptionTarget) this.modeDescriptionTarget.textContent = MODE_DESCRIPTIONS[mode] || "";

    const total = parseFloat(trajectory.totalTargetHt) || 0;
    if (this.hasSummaryTotalTarget) {
      const strong = this.summaryTotalTarget.querySelector('strong') ?? this.summaryTotalTarget;
      strong.textContent = formatEur(total);
    }
    if (this.hasSummaryAvgTarget) this.summaryAvgTarget.textContent = formatEur(total / 12);
    if (this.hasSummaryVsLastTarget) {
      const previousYearTotal = parseFloat(comparison.previousYearTotal);
      if (Number.isFinite(previousYearTotal) && previousYearTotal > 0) {
        const delta = ((total - previousYearTotal) / previousYearTotal) * 100;
        this.summaryVsLastTarget.textContent =
          `${delta >= 0 ? "+ " : "− "}${formatPercent(Math.abs(delta))} (${formatEur(previousYearTotal)} → ${formatEur(total)})`;
      } else {
        this.summaryVsLastTarget.textContent = "—";
      }
    }
    if (this.hasSummaryActivityTarget) {
      const hasBreakdown = trajectory.breakdownByActivity && Object.keys(trajectory.breakdownByActivity).length > 0;
      this.summaryActivityTarget.textContent = hasBreakdown ? "Activée" : "Désactivée";
    }
  }

  showEmptyState() {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.hidden = false;
      this.errorBannerTarget.className = "nm-banner nm-banner--info";
      if (this.hasErrorTitleTarget) this.errorTitleTarget.textContent = `Aucune trajectoire définie pour ${this._year}.`;
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent =
          " Cliquez sur « Modifier ma trajectoire » pour définir votre objectif annuel.";
      }
    }
  }

  showError(message) {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.hidden = false;
      if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = ` ${message}`;
    }
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(value));
}

function formatEur(value) {
  const n = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  return `${n} €`;
}

function formatSignedEur(value) {
  if (!Number.isFinite(value) || value === 0) return formatEur(0);
  const abs = formatEur(Math.abs(value));
  return value < 0 ? `− ${abs}` : `+ ${abs}`;
}

function formatPercent(value) {
  return `${new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} %`;
}

function formatSignedPercent(value) {
  if (!Number.isFinite(value) || value === 0) return "0,0 %";
  const abs = formatPercent(Math.abs(value));
  return value < 0 ? `− ${abs}` : `+ ${abs}`;
}
